import { useCallback, useEffect, useRef, useState, type PointerEvent as ReactPointerEvent, type WheelEvent as ReactWheelEvent } from 'react'
import { motion } from 'framer-motion'
import type { BuildMediaDto } from '../../../models/build.model'
import { isVideo, buildMediaUrl, PlayBadge } from './buildMedia'

/**
 * Full-screen media viewer for the /build product page. Parallel to
 * ShopMediaLightbox; targets the /build/media/:id surface.
 */

const MIN_ZOOM = 1
const MAX_ZOOM = 4
const ZOOM_STEP = 0.5
const DOUBLE_CLICK_ZOOM = 2.5

export function BuildMediaLightbox({
  media,
  productName,
  startIndex,
  onClose
}: {
  readonly media: ReadonlyArray<BuildMediaDto>
  readonly productName: string
  readonly startIndex: number
  readonly onClose: () => void
}): JSX.Element | null {
  const [index, setIndex] = useState(startIndex)
  const [zoom, setZoom] = useState(1)
  const [offset, setOffset] = useState({ x: 0, y: 0 })
  const dragRef = useRef<{ startX: number; startY: number; baseX: number; baseY: number } | null>(null)

  const resetZoom = useCallback((): void => {
    setZoom(1)
    setOffset({ x: 0, y: 0 })
  }, [])

  const go = useCallback(
    (next: number): void => {
      if (media.length === 0) return
      setIndex((next + media.length) % media.length)
      resetZoom()
    },
    [media.length, resetZoom]
  )

  const zoomIn = useCallback((): void => setZoom((z) => Math.min(MAX_ZOOM, z + ZOOM_STEP)), [])
  const zoomOut = useCallback((): void => {
    setZoom((z) => {
      const nz = Math.max(MIN_ZOOM, z - ZOOM_STEP)
      if (nz === 1) setOffset({ x: 0, y: 0 })
      return nz
    })
  }, [])

  useEffect(() => {
    const onKey = (e: KeyboardEvent): void => {
      if (e.key === 'Escape') onClose()
      else if (e.key === 'ArrowRight') go(index + 1)
      else if (e.key === 'ArrowLeft') go(index - 1)
      else if (e.key === '+' || e.key === '=') zoomIn()
      else if (e.key === '-' || e.key === '_') zoomOut()
    }
    window.addEventListener('keydown', onKey)
    return () => window.removeEventListener('keydown', onKey)
  }, [index, go, onClose, zoomIn, zoomOut])

  useEffect(() => {
    const prev = document.body.style.overflow
    document.body.style.overflow = 'hidden'
    return () => {
      document.body.style.overflow = prev
    }
  }, [])

  const active = media[index]
  if (active === undefined) return null
  const activeIsVideo = isVideo(active.content_type)

  const handleWheel = (e: ReactWheelEvent<HTMLImageElement>): void => {
    setZoom((z) => {
      const nz = Math.min(MAX_ZOOM, Math.max(MIN_ZOOM, z - Math.sign(e.deltaY) * ZOOM_STEP))
      if (nz === 1) setOffset({ x: 0, y: 0 })
      return nz
    })
  }

  const handlePointerDown = (e: ReactPointerEvent<HTMLImageElement>): void => {
    if (zoom <= 1) return
    dragRef.current = { startX: e.clientX, startY: e.clientY, baseX: offset.x, baseY: offset.y }
    e.currentTarget.setPointerCapture(e.pointerId)
  }
  const handlePointerMove = (e: ReactPointerEvent<HTMLImageElement>): void => {
    const d = dragRef.current
    if (d === null) return
    setOffset({ x: d.baseX + (e.clientX - d.startX), y: d.baseY + (e.clientY - d.startY) })
  }
  const endDrag = (): void => {
    dragRef.current = null
  }

  const toggleZoom = (): void => {
    if (zoom > 1) {
      resetZoom()
    } else {
      setZoom(DOUBLE_CLICK_ZOOM)
    }
  }

  const stop = (e: { stopPropagation: () => void }): void => e.stopPropagation()

  return (
    <motion.div
      className="fixed inset-0 z-50 flex flex-col bg-black/90 backdrop-blur-sm"
      initial={{ opacity: 0 }}
      animate={{ opacity: 1 }}
      role="dialog"
      aria-modal="true"
      aria-label={`${productName} media viewer`}
      onClick={onClose}
    >
      <div className="flex items-center justify-between px-4 py-3 text-text-light" onClick={stop}>
        <span className="font-body text-body-sm opacity-80">
          {index + 1} / {media.length}
        </span>
        <div className="flex items-center gap-2">
          {!activeIsVideo ? (
            <>
              <RoundButton label="Zoom out" onClick={zoomOut} disabled={zoom <= MIN_ZOOM}>
                <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" aria-hidden>
                  <path d="M5 12h14" />
                </svg>
              </RoundButton>
              <span className="w-12 text-center font-body text-body-sm tabular-nums opacity-80">
                {Math.round(zoom * 100)}%
              </span>
              <RoundButton label="Zoom in" onClick={zoomIn} disabled={zoom >= MAX_ZOOM}>
                <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" aria-hidden>
                  <path d="M12 5v14M5 12h14" />
                </svg>
              </RoundButton>
            </>
          ) : null}
          <RoundButton label="Close" onClick={onClose}>
            <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" aria-hidden>
              <path d="M6 6l12 12M18 6L6 18" />
            </svg>
          </RoundButton>
        </div>
      </div>

      <div className="relative flex flex-1 items-center justify-center overflow-hidden px-4">
        {media.length > 1 ? (
          <StageArrow dir="prev" onClick={(e) => { stop(e); go(index - 1) }} />
        ) : null}

        {activeIsVideo ? (
          <video
            key={active.media_id}
            src={buildMediaUrl(active.media_id)}
            controls
            autoPlay
            playsInline
            onClick={stop}
            className="max-h-full max-w-full"
          />
        ) : (
          <img
            key={active.media_id}
            src={buildMediaUrl(active.media_id)}
            alt={active.alt_text ?? productName}
            draggable={false}
            onClick={stop}
            onWheel={handleWheel}
            onPointerDown={handlePointerDown}
            onPointerMove={handlePointerMove}
            onPointerUp={endDrag}
            onPointerCancel={endDrag}
            onDoubleClick={toggleZoom}
            style={{
              transform: `translate(${offset.x}px, ${offset.y}px) scale(${zoom})`,
              cursor: zoom > 1 ? 'grab' : 'zoom-in',
              touchAction: 'none'
            }}
            className="max-h-full max-w-full select-none object-contain"
          />
        )}

        {media.length > 1 ? (
          <StageArrow dir="next" onClick={(e) => { stop(e); go(index + 1) }} />
        ) : null}
      </div>

      {media.length > 1 ? (
        <div className="flex justify-center gap-2 overflow-x-auto px-4 py-4" onClick={stop}>
          {media.map((m, i) => (
            <button
              key={m.media_id}
              type="button"
              onClick={() => go(i)}
              aria-label={`View item ${i + 1}`}
              className={`relative h-16 w-16 flex-shrink-0 overflow-hidden rounded-lg border transition ${
                i === index ? 'border-white ring-1 ring-white' : 'border-white/30 hover:border-white/70'
              }`}
            >
              {isVideo(m.content_type) ? (
                <>
                  <video src={`${buildMediaUrl(m.media_id)}#t=0.1`} muted preload="metadata" className="h-full w-full bg-black object-cover" />
                  <span className="absolute inset-0 flex items-center justify-center bg-black/25">
                    <PlayBadge />
                  </span>
                </>
              ) : (
                <img src={buildMediaUrl(m.media_id)} alt={m.alt_text ?? `${productName} thumbnail ${i + 1}`} className="h-full w-full object-cover" />
              )}
            </button>
          ))}
        </div>
      ) : null}
    </motion.div>
  )
}

function RoundButton({
  label,
  onClick,
  disabled = false,
  children
}: {
  readonly label: string
  readonly onClick: () => void
  readonly disabled?: boolean
  readonly children: React.ReactNode
}): JSX.Element {
  return (
    <button
      type="button"
      onClick={onClick}
      disabled={disabled}
      aria-label={label}
      title={label}
      className="flex h-9 w-9 items-center justify-center rounded-full border border-white/30 bg-white/10 text-text-light transition hover:bg-white/20 disabled:opacity-30"
    >
      {children}
    </button>
  )
}

function StageArrow({
  dir,
  onClick
}: {
  readonly dir: 'prev' | 'next'
  readonly onClick: (e: ReactPointerEvent<HTMLButtonElement> | { stopPropagation: () => void }) => void
}): JSX.Element {
  const isPrev = dir === 'prev'
  return (
    <button
      type="button"
      onClick={onClick}
      aria-label={isPrev ? 'Previous item' : 'Next item'}
      className={`absolute top-1/2 z-10 ${isPrev ? 'left-3' : 'right-3'} flex h-11 w-11 -translate-y-1/2 items-center justify-center rounded-full border border-white/30 bg-white/10 text-text-light transition hover:bg-white/20`}
    >
      <svg width="22" height="22" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" aria-hidden>
        {isPrev ? <path d="M15 18l-6-6 6-6" /> : <path d="M9 18l6-6-6-6" />}
      </svg>
    </button>
  )
}
